package com.algorix.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

enum class Lang(val code: String) { EN("en"), TR("tr") }

/** Current language. Every text read through [t] recomposes when it changes. */
object I18n {
    private const val STORE_KEY = "algroix-lang"

    var lang by mutableStateOf(Lang.EN)
        private set

    fun t(key: String): String =
        (if (lang == Lang.TR) Strings.tr[key] else null) ?: Strings.en[key] ?: ""

    fun apply(l: Lang) {
        lang = l
        document.documentElement?.setAttribute("lang", l.code)
    }

    /** Called from the TR/EN switch: remembers the choice and keeps a #en / #tr hash in sync. */
    fun choose(l: Lang) {
        apply(l)
        try {
            window.localStorage.setItem(STORE_KEY, l.code)
        } catch (_: Throwable) {
        }
        try {
            if (fromHash() != null) {
                window.history.replaceState(null, "", window.location.pathname + window.location.search + "#" + l.code)
            }
        } catch (_: Throwable) {
        }
    }

    fun init() {
        apply(initial())
        window.addEventListener("hashchange", { fromHash()?.let(::apply) })
    }

    private fun parse(s: String?): Lang? = when (s?.lowercase()) {
        "tr" -> Lang.TR
        "en" -> Lang.EN
        else -> null
    }

    private fun fromHash(): Lang? = parse(window.location.hash.removePrefix("#"))

    private fun initial(): Lang {
        fromHash()?.let { return it }
        try {
            parse(window.localStorage.getItem(STORE_KEY))?.let { return it }
        } catch (_: Throwable) {
        }
        val nav = try {
            window.navigator.language.lowercase()
        } catch (_: Throwable) {
            ""
        }
        return if (nav.startsWith("tr")) Lang.TR else Lang.EN
    }
}

fun t(key: String): String = I18n.t(key)

/** Keeps product and protocol names from breaking at a hyphen or space. */
private val NOWRAP = Regex("Wi-Fi|XChaCha20-Poly1305|App Store|Google Play")

@Composable
fun NwText(value: String) {
    var last = 0
    for (m in NOWRAP.findAll(value)) {
        if (m.range.first > last) Text(value.substring(last, m.range.first))
        Span({ classes("nw") }) { Text(m.value) }
        last = m.range.last + 1
    }
    if (last < value.length) Text(value.substring(last))
}

/** Translated text for [key], with the no-wrap treatment. */
@Composable
fun Tx(key: String) = NwText(t(key))

private val CODE = Regex("<code>(.*?)</code>")

/** Translated text that may contain inline `<code>` runs. */
@Composable
fun RichText(key: String) {
    val value = t(key)
    var last = 0
    for (m in CODE.findAll(value)) {
        if (m.range.first > last) Text(value.substring(last, m.range.first))
        Code { Text(m.groupValues[1]) }
        last = m.range.last + 1
    }
    if (last < value.length) Text(value.substring(last))
}
